import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Crown, Plus, Trash2, Power, Copy, Zap, Store, Radio, Image as ImageIcon, Award, Percent, Ticket, Save, Star, TrendingUp, UserCheck } from 'lucide-react';

const FEATURE_DEFS = [
    { key: 'prime_listing_enabled', label: 'Publicación Prime', icon: Crown, hasLimit: 'prime_listings_per_month', limitLabel: 'anuncios/mes (-1=ilimitados)' },
    { key: 'flash_sales_enabled', label: 'Ofertas relámpago', icon: Zap, hasLimit: 'flash_sales_per_month', limitLabel: '/mes' },
    { key: 'okstore_enabled', label: 'OkStore (tienda individual)', icon: Store, extra: 'okstore_branding_advanced', extraLabel: 'Branding avanzado' },
    { key: 'oklive_enabled', label: 'OkLive Shop', icon: Radio, hasLimit: 'oklive_minutes_per_month', limitLabel: 'min/mes' },
    { key: 'banners_enabled', label: 'Banners', icon: ImageIcon, hasLimit: 'banner_slots', limitLabel: 'slots' },
    { key: 'okpro_enabled', label: 'OkPro (boost)', icon: Award, hasLimit: 'boost_credits_per_month', limitLabel: 'créd/mes' },
    { key: 'services_provider_enabled', label: 'Lavandería / Tintorería', icon: UserCheck, hasLimit: 'services_max_kinds', limitLabel: 'tipos (1=un tipo, 2=ambos)' },
    { key: 'priority_support', label: 'Soporte prioritario', icon: Star },
    { key: 'verified_badge', label: 'Insignia verificado', icon: Award },
];

const EMPTY_PLAN = {
    name: '', slug: '', tagline: '', description: '',
    price_monthly_mxn: 0, price_yearly_mxn: 0, trial_days: 0,
    active: true, highlighted: false, order: 99,
    features: {
        prime_listing_enabled: false, prime_listings_per_month: -1,
        gratuita_listing_duration_days: 90, gratuita_max_per_year: 5,
        flash_sales_enabled: false, flash_sales_per_month: 0,
        okstore_enabled: false, okstore_branding_advanced: false,
        oklive_enabled: false, oklive_minutes_per_month: 0,
        banners_enabled: false, banner_slots: 0,
        okpro_enabled: false, boost_credits_per_month: 0,
        services_provider_enabled: false, services_max_kinds: 1,
        commission_percent: 8, cashback_max_percent: 5,
        priority_support: false, verified_badge: false,
    },
};

export default function AdminPlans() {
    const [tab, setTab] = useState('plans');
    const [plans, setPlans] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [couponForm, setCouponForm] = useState({ code: '', percent_off: 10, max_uses: 100, applicable_plan_slugs: [] });
    const [showCouponForm, setShowCouponForm] = useState(false);
    const [mrr, setMrr] = useState(null);
    const [users, setUsers] = useState([]);
    const [userQuery, setUserQuery] = useState('');
    const [assignTarget, setAssignTarget] = useState(null); // user obj
    const [assignForm, setAssignForm] = useState({ slug: 'free', billing: 'monthly' });

    const load = useCallback(async () => {
        try {
            const [{ data: p }, { data: c }, { data: m }] = await Promise.all([
                api.get('/admin/plans'),
                api.get('/admin/plans/coupons'),
                api.get('/admin/plans/mrr').catch(() => ({ data: null })),
            ]);
            setPlans(p.items || []);
            setCoupons(c.items || []);
            setMrr(m);
        } catch (e) { toast.error('Error al cargar'); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const loadUsers = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/users', { params: userQuery ? { q: userQuery } : {} });
            setUsers((data || []).slice(0, 200));
        } catch { toast.error('Error usuarios'); }
    }, [userQuery]);

    useEffect(() => { if (tab === 'users') loadUsers(); }, [tab, loadUsers]);

    const assignPlanToUser = async () => {
        if (!assignTarget) return;
        try {
            await api.post(`/admin/users/${assignTarget.id}/assign-plan`, assignForm);
            toast.success(`Plan ${assignForm.slug} asignado a @${assignTarget.nickname}`);
            setAssignTarget(null);
            loadUsers();
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const savePlan = async () => {
        setBusy(true);
        try {
            const payload = { ...editing };
            payload.price_monthly_mxn = parseFloat(payload.price_monthly_mxn) || 0;
            payload.price_yearly_mxn = parseFloat(payload.price_yearly_mxn) || 0;
            payload.trial_days = parseInt(payload.trial_days) || 0;
            payload.order = parseInt(payload.order) || 0;
            payload.features.flash_sales_per_month = parseInt(payload.features.flash_sales_per_month) || 0;
            payload.features.oklive_minutes_per_month = parseInt(payload.features.oklive_minutes_per_month) || 0;
            payload.features.banner_slots = parseInt(payload.features.banner_slots) || 0;
            payload.features.boost_credits_per_month = parseInt(payload.features.boost_credits_per_month) || 0;
            payload.features.commission_percent = parseFloat(payload.features.commission_percent) || 0;
            payload.features.cashback_max_percent = parseFloat(payload.features.cashback_max_percent) || 0;
            payload.features.gratuita_listing_duration_days = parseInt(payload.features.gratuita_listing_duration_days) || 90;
            payload.features.gratuita_max_per_year = parseInt(payload.features.gratuita_max_per_year) || 5;
            payload.features.prime_listings_per_month = parseInt(payload.features.prime_listings_per_month ?? -1);
            payload.features.services_max_kinds = parseInt(payload.features.services_max_kinds) || 1;
            if (editing.id) {
                await api.put(`/admin/plans/${editing.id}`, payload);
            } else {
                await api.post('/admin/plans', payload);
            }
            toast.success('Plan guardado');
            setEditing(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); } finally { setBusy(false); }
    };

    const deletePlan = async (id) => {
        if (!window.confirm('¿Eliminar este plan? Esta acción no se puede deshacer.')) return;
        try {
            await api.delete(`/admin/plans/${id}`);
            toast.success('Plan eliminado');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const togglePlan = async (id) => {
        try {
            await api.post(`/admin/plans/${id}/toggle`);
            load();
        } catch (e) { toast.error('Error'); }
    };

    const duplicatePlan = (p) => {
        const copy = { ...p, name: `${p.name} (copia)`, slug: `${p.slug}-copy`, highlighted: false, order: 99 };
        delete copy.id;
        delete copy.created_at;
        delete copy.updated_at;
        setEditing(copy);
    };

    const saveCoupon = async () => {
        setBusy(true);
        try {
            await api.post('/admin/plans/coupons', couponForm);
            toast.success('Cupón creado');
            setShowCouponForm(false);
            setCouponForm({ code: '', percent_off: 10, max_uses: 100, applicable_plan_slugs: [] });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const deleteCoupon = async (id) => {
        if (!window.confirm('¿Eliminar cupón?')) return;
        try { await api.delete(`/admin/plans/coupons/${id}`); load(); } catch { toast.error('Error'); }
    };

    const toggleCoupon = async (id) => {
        try { await api.post(`/admin/plans/coupons/${id}/toggle`); load(); } catch { toast.error('Error'); }
    };

    return (
        <div className="space-y-6" data-testid="admin-plans-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Crown className="h-6 w-6 text-[hsl(var(--primary))]" />
                    <h1 className="text-2xl font-semibold">Planes OkGo</h1>
                </div>
                <Button onClick={() => setEditing({ ...EMPTY_PLAN })} data-testid="plan-create-btn">
                    <Plus className="h-4 w-4 mr-1" />Nuevo plan
                </Button>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="plans" data-testid="plans-tab">Planes ({plans.length})</TabsTrigger>
                    <TabsTrigger value="coupons" data-testid="coupons-tab">Cupones ({coupons.length})</TabsTrigger>
                    <TabsTrigger value="mrr" data-testid="mrr-tab"><TrendingUp className="h-3.5 w-3.5 mr-1" />MRR</TabsTrigger>
                    <TabsTrigger value="users" data-testid="users-tab"><UserCheck className="h-3.5 w-3.5 mr-1" />Asignar plan</TabsTrigger>
                </TabsList>

                <TabsContent value="plans" className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {plans.length === 0 && <div className="col-span-full text-sm text-muted-foreground py-10 text-center">Sin planes.</div>}
                        {plans.map(p => (
                            <Card key={p.id} data-testid={`plan-card-${p.slug}`} className={p.highlighted ? 'border-[hsl(var(--primary))] shadow-md' : ''}>
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <div className="text-lg font-semibold">{p.name}</div>
                                                {p.highlighted && <Badge className="bg-[hsl(var(--primary))] text-white">Popular</Badge>}
                                            </div>
                                            <div className="text-xs text-muted-foreground">/{p.slug}</div>
                                        </div>
                                        {p.active ? <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}
                                    </div>
                                    {p.tagline && <div className="text-xs text-muted-foreground italic mt-1">{p.tagline}</div>}
                                    <div className="my-3 flex items-baseline gap-2">
                                        <div className="text-3xl font-bold">${p.price_monthly_mxn}</div>
                                        <div className="text-xs text-muted-foreground">/mes · ${p.price_yearly_mxn}/año</div>
                                    </div>
                                    {p.trial_days > 0 && <Badge variant="outline" className="text-xs">{p.trial_days} días gratis</Badge>}
                                    <div className="mt-3 space-y-1 text-xs">
                                        {FEATURE_DEFS.filter(f => p.features?.[f.key]).map(f => {
                                            const Icon = f.icon;
                                            return (
                                                <div key={f.key} className="flex items-center gap-1 text-muted-foreground">
                                                    <Icon className="h-3 w-3 text-emerald-600" />
                                                    <span>{f.label}</span>
                                                    {f.hasLimit && p.features[f.hasLimit] > 0 && (
                                                        <span className="font-medium">({p.features[f.hasLimit]}{f.limitLabel})</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <div className="flex items-center gap-1 text-muted-foreground">
                                            <Percent className="h-3 w-3 text-blue-600" />Comisión: <span className="font-medium">{p.features?.commission_percent}%</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 mt-4">
                                        <Button size="sm" variant="outline" className="flex-1" onClick={() => setEditing({ ...p })} data-testid={`plan-edit-${p.slug}`}>Editar</Button>
                                        <Button size="sm" variant="ghost" onClick={() => togglePlan(p.id)} title={p.active ? 'Desactivar' : 'Activar'}><Power className="h-4 w-4" /></Button>
                                        <Button size="sm" variant="ghost" onClick={() => duplicatePlan(p)} title="Duplicar"><Copy className="h-4 w-4" /></Button>
                                        <Button size="sm" variant="ghost" onClick={() => deletePlan(p.id)} title="Eliminar"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="coupons" className="pt-4 space-y-3">
                    <div className="flex justify-end">
                        <Button size="sm" onClick={() => setShowCouponForm(true)} data-testid="coupon-create-btn"><Plus className="h-4 w-4 mr-1" />Nuevo cupón</Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {coupons.length === 0 && <div className="col-span-full text-sm text-muted-foreground py-10 text-center">Sin cupones aún.</div>}
                        {coupons.map(c => (
                            <Card key={c.id} data-testid={`coupon-${c.code}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="font-mono font-bold text-lg">{c.code}</div>
                                            <div className="text-2xl font-semibold text-[hsl(var(--primary))]">-{c.percent_off}%</div>
                                            <div className="text-xs text-muted-foreground mt-1">Usos: {c.used || 0} / {c.max_uses}</div>
                                            {c.applicable_plan_slugs?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {c.applicable_plan_slugs.map(s => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                                                </div>
                                            )}
                                        </div>
                                        {c.active ? <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                        <Button size="sm" variant="outline" className="flex-1" onClick={() => toggleCoupon(c.id)}><Power className="h-3 w-3" /></Button>
                                        <Button size="sm" variant="ghost" onClick={() => deleteCoupon(c.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="mrr" className="pt-4">
                    {!mrr ? (
                        <div className="text-sm text-muted-foreground py-10 text-center">Cargando MRR…</div>
                    ) : (
                        <div className="space-y-4" data-testid="mrr-content">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <Card><CardContent className="p-4">
                                    <div className="text-xs uppercase text-muted-foreground">MRR (mensual recurrente)</div>
                                    <div className="text-3xl font-bold text-emerald-700 mt-1" data-testid="mrr-value">${(mrr.mrr || 0).toLocaleString('es-MX')}</div>
                                    <div className="text-xs text-muted-foreground mt-1">MXN estimado por mes</div>
                                </CardContent></Card>
                                <Card><CardContent className="p-4">
                                    <div className="text-xs uppercase text-muted-foreground">ARR (anual)</div>
                                    <div className="text-3xl font-bold mt-1" data-testid="arr-value">${(mrr.arr || 0).toLocaleString('es-MX')}</div>
                                    <div className="text-xs text-muted-foreground mt-1">MRR × 12</div>
                                </CardContent></Card>
                                <Card><CardContent className="p-4">
                                    <div className="text-xs uppercase text-muted-foreground">Suscriptores activos</div>
                                    <div className="text-3xl font-bold mt-1">{(mrr.breakdown || []).reduce((acc, b) => acc + b.users, 0)}</div>
                                    <div className="text-xs text-muted-foreground mt-1">Total en {(mrr.breakdown || []).length} combinaciones</div>
                                </CardContent></Card>
                            </div>
                            <Card><CardContent className="p-4">
                                <h3 className="text-sm font-semibold mb-3">Desglose por plan</h3>
                                <table className="w-full text-sm">
                                    <thead><tr className="text-left text-xs text-muted-foreground border-b">
                                        <th className="py-2">Plan</th><th>Ciclo</th><th className="text-right">Usuarios</th><th className="text-right">Ingreso mensual</th>
                                    </tr></thead>
                                    <tbody>
                                        {(mrr.breakdown || []).length === 0 && <tr><td colSpan="4" className="py-4 text-center text-muted-foreground">Sin suscriptores todavía.</td></tr>}
                                        {(mrr.breakdown || []).map((b, i) => (
                                            <tr key={i} className="border-b last:border-0">
                                                <td className="py-2 font-medium">{b.plan_name}</td>
                                                <td>{b.billing === 'yearly' ? 'Anual' : 'Mensual'}</td>
                                                <td className="text-right">{b.users}</td>
                                                <td className="text-right font-mono">${b.monthly_revenue.toLocaleString('es-MX')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent></Card>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="users" className="pt-4 space-y-3" data-testid="users-content">
                    <div className="flex gap-2 items-center">
                        <Input placeholder="Buscar usuario (nickname/email/nombre)" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadUsers()} data-testid="users-search" />
                        <Button variant="outline" size="sm" onClick={loadUsers}>Buscar</Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
                                <tr><th className="p-2 text-left">Usuario</th><th className="text-left">Email</th><th className="text-left">Plan actual</th><th className="text-left">Ciclo</th><th></th></tr>
                            </thead>
                            <tbody>
                                {users.length === 0 && <tr><td colSpan="5" className="p-4 text-center text-muted-foreground">Sin usuarios.</td></tr>}
                                {users.map(u => (
                                    <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50">
                                        <td className="p-2 font-medium">@{u.nickname}</td>
                                        <td className="text-xs text-muted-foreground">{u.email}</td>
                                        <td><Badge variant="outline" className="text-[10px]">{u.plan?.slug || 'sin plan'}</Badge></td>
                                        <td className="text-xs">{u.plan?.billing || '-'}</td>
                                        <td className="text-right pr-2">
                                            <Button size="sm" variant="outline" onClick={() => { setAssignTarget(u); setAssignForm({ slug: u.plan?.slug || 'free', billing: u.plan?.billing || 'monthly' }); }} data-testid={`assign-plan-${u.nickname}`}>Cambiar plan</Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Plan editor */}
            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plan-editor-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? 'Editar plan' : 'Crear nuevo plan'}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Nombre público</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="plan-edit-name" /></div>
                                <div><Label>Slug (URL)</Label><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} data-testid="plan-edit-slug" /></div>
                            </div>
                            <div><Label>Tagline</Label><Input value={editing.tagline || ''} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} placeholder="Ej: El plan más popular" /></div>
                            <div><Label>Descripción</Label><Textarea rows={2} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
                            <div className="grid grid-cols-4 gap-3">
                                <div><Label>Precio mensual $</Label><Input type="number" value={editing.price_monthly_mxn} onChange={(e) => setEditing({ ...editing, price_monthly_mxn: e.target.value })} data-testid="plan-edit-price-monthly" /></div>
                                <div><Label>Precio anual $</Label><Input type="number" value={editing.price_yearly_mxn} onChange={(e) => setEditing({ ...editing, price_yearly_mxn: e.target.value })} /></div>
                                <div><Label>Días gratis</Label><Input type="number" value={editing.trial_days} onChange={(e) => setEditing({ ...editing, trial_days: e.target.value })} data-testid="plan-edit-trial" /></div>
                                <div><Label>Orden</Label><Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
                            </div>
                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2"><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><Label>Activo</Label></div>
                                <div className="flex items-center gap-2"><Switch checked={!!editing.highlighted} onCheckedChange={(v) => setEditing({ ...editing, highlighted: v })} /><Label>Destacar</Label></div>
                            </div>

                            <div className="border-t pt-3">
                                <h3 className="text-sm font-semibold mb-3">Funcionalidades incluidas</h3>
                                <div className="space-y-3">
                                    {FEATURE_DEFS.map(f => {
                                        const Icon = f.icon;
                                        return (
                                            <div key={f.key} className="p-3 rounded-lg border">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2"><Icon className="h-4 w-4" /><span className="text-sm font-medium">{f.label}</span></div>
                                                    <Switch checked={!!editing.features[f.key]} onCheckedChange={(v) => setEditing({ ...editing, features: { ...editing.features, [f.key]: v } })} data-testid={`plan-feat-${f.key}`} />
                                                </div>
                                                {editing.features[f.key] && f.hasLimit && (
                                                    <div className="mt-2 flex items-center gap-2">
                                                        <Label className="text-xs">Límite {f.limitLabel}:</Label>
                                                        <Input type="number" value={editing.features[f.hasLimit]} onChange={(e) => setEditing({ ...editing, features: { ...editing.features, [f.hasLimit]: e.target.value } })} className="h-8 max-w-[120px]" />
                                                    </div>
                                                )}
                                                {editing.features[f.key] && f.extra && (
                                                    <div className="mt-2 flex items-center gap-2">
                                                        <Switch checked={!!editing.features[f.extra]} onCheckedChange={(v) => setEditing({ ...editing, features: { ...editing.features, [f.extra]: v } })} />
                                                        <Label className="text-xs">{f.extraLabel}</Label>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {/* Listing type config */}
                                    <div className="p-3 rounded-lg border bg-amber-50/50 border-amber-200 space-y-3">
                                        <div className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                                            <Crown className="h-4 w-4 text-amber-600" />Tipos de publicación
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <Label className="text-xs">Gratuita: duración (días)</Label>
                                                <Input type="number" min="1" value={editing.features.gratuita_listing_duration_days ?? 90}
                                                    onChange={e => setEditing({ ...editing, features: { ...editing.features, gratuita_listing_duration_days: e.target.value } })}
                                                    className="mt-1 h-8 text-sm" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Gratuita: máx. por año</Label>
                                                <Input type="number" min="1" value={editing.features.gratuita_max_per_year ?? 5}
                                                    onChange={e => setEditing({ ...editing, features: { ...editing.features, gratuita_max_per_year: e.target.value } })}
                                                    className="mt-1 h-8 text-sm" />
                                            </div>
                                        </div>
                                        <p className="text-xs text-amber-700">Gratuita: 0% comisión. Prime: comisión según tabla de categorías (ver sección Comisiones).</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                                        <div><Label>Comisión plataforma %</Label><Input type="number" step="0.5" value={editing.features.commission_percent} onChange={(e) => setEditing({ ...editing, features: { ...editing.features, commission_percent: e.target.value } })} /></div>
                                        <div><Label>Cashback máx %</Label><Input type="number" step="0.5" value={editing.features.cashback_max_percent} onChange={(e) => setEditing({ ...editing, features: { ...editing.features, cashback_max_percent: e.target.value } })} /></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
                        <Button onClick={savePlan} disabled={busy || !editing?.name || !editing?.slug} data-testid="plan-save-btn">
                            <Save className="h-4 w-4 mr-1" />{busy ? 'Guardando…' : (editing?.id ? 'Guardar cambios' : 'Crear plan')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Coupon form */}
            <Dialog open={showCouponForm} onOpenChange={setShowCouponForm}>
                <DialogContent data-testid="coupon-editor-dialog">
                    <DialogHeader><DialogTitle>Crear cupón</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Código (mayúsculas)</Label><Input value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })} placeholder="WELCOME50" data-testid="coupon-code" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>% descuento</Label><Input type="number" min="1" max="100" value={couponForm.percent_off} onChange={(e) => setCouponForm({ ...couponForm, percent_off: parseFloat(e.target.value) || 0 })} data-testid="coupon-percent" /></div>
                            <div><Label>Usos máx</Label><Input type="number" min="1" value={couponForm.max_uses} onChange={(e) => setCouponForm({ ...couponForm, max_uses: parseInt(e.target.value) || 0 })} /></div>
                        </div>
                        <div>
                            <Label className="mb-1 block">Aplica a planes (vacío = todos)</Label>
                            <div className="flex flex-wrap gap-2">
                                {plans.map(p => {
                                    const selected = couponForm.applicable_plan_slugs.includes(p.slug);
                                    return (
                                        <button key={p.slug} type="button" onClick={() => setCouponForm(cf => ({
                                            ...cf,
                                            applicable_plan_slugs: selected ? cf.applicable_plan_slugs.filter(s => s !== p.slug) : [...cf.applicable_plan_slugs, p.slug],
                                        }))} className={`text-xs px-2 py-1 rounded border ${selected ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'bg-card border-slate-300'}`}>
                                            {p.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowCouponForm(false)}>Cancelar</Button>
                        <Button onClick={saveCoupon} disabled={busy || !couponForm.code} data-testid="coupon-save-btn">Crear cupón</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Assign plan dialog */}
            <Dialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)}>
                <DialogContent data-testid="assign-plan-dialog">
                    <DialogHeader>
                        <DialogTitle>Asignar plan a @{assignTarget?.nickname}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Plan</Label>
                            <div className="grid grid-cols-1 gap-2 mt-2">
                                {plans.map(p => (
                                    <button
                                        key={p.slug}
                                        type="button"
                                        onClick={() => setAssignForm(f => ({ ...f, slug: p.slug }))}
                                        className={`flex items-center justify-between px-3 py-2 rounded border text-left ${assignForm.slug === p.slug ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.1)]' : 'border-slate-300'}`}
                                        data-testid={`assign-slug-${p.slug}`}
                                    >
                                        <div>
                                            <div className="font-medium text-sm">{p.name}</div>
                                            <div className="text-xs text-muted-foreground">${p.price_monthly_mxn}/mes · ${p.price_yearly_mxn}/año</div>
                                        </div>
                                        {p.highlighted && <Badge className="bg-[hsl(var(--primary))] text-white text-[10px]">Popular</Badge>}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Ciclo</Label>
                            <div className="flex gap-2 mt-1">
                                <button type="button" onClick={() => setAssignForm(f => ({ ...f, billing: 'monthly' }))} className={`flex-1 py-2 rounded border text-sm ${assignForm.billing === 'monthly' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.1)] font-medium' : 'border-slate-300'}`}>Mensual</button>
                                <button type="button" onClick={() => setAssignForm(f => ({ ...f, billing: 'yearly' }))} className={`flex-1 py-2 rounded border text-sm ${assignForm.billing === 'yearly' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.1)] font-medium' : 'border-slate-300'}`}>Anual</button>
                            </div>
                        </div>
                        <div className="text-xs p-2 rounded bg-amber-50 text-amber-800 border border-amber-200">
                            ⚠️ Esta acción es de superadmin y no cobra al usuario ni consume cupones. El plan quedará marcado como `assigned_by_admin`.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setAssignTarget(null)}>Cancelar</Button>
                        <Button onClick={assignPlanToUser} data-testid="assign-plan-confirm">Asignar plan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
